import { useEffect, useState } from "react";
import {
  regions as regionsService,
  regionCountries as regionCountriesService,
  type Region,
  type RegionNode,
} from "../api/regions.service";

/**
 * Fetch the full taxonomy regions list once and cache it module-side for the
 * lifetime of the SPA. Regions are taxonomy data, so a single fetch is fine.
 *
 * Returns the flat list (sorted by name) plus loading / error state. Consumers
 * that need a tree should call {@link buildRegionTree} on the result.
 */
let cachedPromise: Promise<Region[]> | null = null;
let cachedValue: Region[] | null = null;

export function __resetRegionsCache() {
  cachedPromise = null;
  cachedValue = null;
}

function fetchOnce(): Promise<Region[]> {
  if (cachedValue) return Promise.resolve(cachedValue);
  if (cachedPromise) return cachedPromise;
  cachedPromise = regionsService
    .getAll()
    .then(rows => {
      const sorted = [...rows].sort((a, b) => a.name.localeCompare(b.name));
      cachedValue = sorted;
      return sorted;
    })
    .catch(err => {
      cachedPromise = null;
      throw err;
    });
  return cachedPromise;
}

export type UseRegionsResult = {
  regions: Region[];
  loading: boolean;
  error: Error | null;
};

export function useRegions(): UseRegionsResult {
  const [regions, setRegions] = useState<Region[]>(cachedValue ?? []);
  const [loading, setLoading] = useState(!cachedValue);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (cachedValue) {
      setRegions(cachedValue);
      setLoading(false);
      return;
    }
    fetchOnce()
      .then(rows => {
        if (cancelled) return;
        setRegions(rows);
        setError(null);
      })
      .catch(err => {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { regions, loading, error };
}

export type RegionTreeNode = Region & { children: RegionTreeNode[] };

/**
 * Build a parent/child tree from the flat regions list. Nodes whose parent is
 * missing (orphans) are surfaced at the top level so the tree never silently
 * drops data.
 */
export function buildRegionTree(flat: Region[]): RegionTreeNode[] {
  const byId = new Map<string, RegionTreeNode>();
  for (const r of flat) {
    byId.set(r.id, { ...r, children: [] });
  }
  const roots: RegionTreeNode[] = [];
  for (const node of byId.values()) {
    const parentId = node.parentId;
    if (parentId && byId.has(parentId)) {
      byId.get(parentId)!.children.push(node);
    } else {
      roots.push(node);
    }
  }
  // Sort children alphabetically for stable rendering.
  const sortRec = (nodes: RegionTreeNode[]) => {
    nodes.sort((a, b) => a.name.localeCompare(b.name));
    for (const n of nodes) sortRec(n.children);
  };
  sortRec(roots);
  return roots;
}

// ============================================================================
// Profiles region nodes: `api_region_country_v2` via region-countries tree.
// These are SEPARATE from the UUID `useRegions`/`buildRegionTree` above which
// power the Regions admin CRUD. The profile pickers/forms key identity by
// `${kind}:${id}` because region-ids and country-ids share no namespace.
// ============================================================================

/** Minimal `(kind, id)` identity shape accepted by {@link regionNodeKey}. */
type RegionNodeKeyInput = { kind: RegionNode["kind"]; id: number };

/** Stable identity string for a region node, e.g. `region:42` / `country:7`. */
export function regionNodeKey(node: RegionNodeKeyInput): string {
  return `${node.kind}:${node.id}`;
}

/**
 * Narrow a raw `regionKind` URL param to the `"region" | "country"` discriminator
 * used by the §3.4 list filter. Returns `undefined` for any other value so a
 * stray kind is dropped (and the list service then omits the region filter,
 * since both params are required together).
 */
export function parseRegionKind(raw: string | null | undefined): RegionNode["kind"] | undefined {
  return raw === "region" || raw === "country" ? raw : undefined;
}

// Per-site cache. The unfiltered (full) tree lives under ALL_SITES_KEY; each
// website-scoped tree is cached under its `legacy_publications.site` domain so
// switching Website in a profile form doesn't re-hit the network on the way
// back. Keying the cache by site is the module-cache equivalent of putting
// `site` in a React Query key (spec 19 §4).
const ALL_SITES_KEY = "__all__";
const cachedNodesPromises = new Map<string, Promise<RegionNode[]>>();
const cachedNodesValues = new Map<string, RegionNode[]>();

export function __resetRegionNodesCache() {
  cachedNodesPromises.clear();
  cachedNodesValues.clear();
}

/**
 * Fetch (and cache) the region/country tree for `site`, applying the spec 19 §4
 * fallback: if a site-scoped fetch yields ZERO countries (e.g. a preview env
 * whose `legacy_publications` haven't been WP-synced), fall back to the full
 * unfiltered tree and warn, rather than rendering an empty picker.
 */
function fetchNodesOnce(site?: string): Promise<RegionNode[]> {
  const key = site ?? ALL_SITES_KEY;
  const cached = cachedNodesValues.get(key);
  if (cached) return Promise.resolve(cached);
  const inFlight = cachedNodesPromises.get(key);
  if (inFlight) return inFlight;

  const promise = regionCountriesService
    .getTree(site)
    .then(async rows => {
      if (site && rows.filter(n => n.kind === "country").length === 0) {
        // biome-ignore lint/suspicious/noConsole: spec 19 §4 requires a warn when the site-scoped tree is empty and we fall back
        console.warn(
          `[useRegionNodes] region-countries returned no countries for site "${site}"; falling back to the full tree.`,
        );
        // Do NOT cache the fallback under the site key — it isn't a real
        // site-scoped result. Reuse/cache the full tree under ALL_SITES_KEY and
        // drop this site's in-flight entry so a later request re-checks the site
        // once its publications are synced.
        const full = await fetchNodesOnce();
        cachedNodesPromises.delete(key);
        return full;
      }
      cachedNodesValues.set(key, rows);
      return rows;
    })
    .catch(err => {
      cachedNodesPromises.delete(key);
      throw err;
    });
  cachedNodesPromises.set(key, promise);
  return promise;
}

export type UseRegionNodesResult = {
  nodes: RegionNode[];
  loading: boolean;
  error: Error | null;
};

/**
 * Fetch the profile region/country tree (`region-countries?tree=true`) and cache
 * it for the SPA lifetime. Used by the profile region pickers/forms.
 *
 * Pass `site` (a `legacy_publications.site` domain, resolved from the profile's
 * selected Website via `WEBSITE_OPTIONS.domain`) to scope the tree to the
 * countries that website publishes (spec 19). Omitting it returns the full
 * tree. Each distinct `site` is fetched and cached independently.
 */
export function useRegionNodes(site?: string): UseRegionNodesResult {
  const cacheKey = site ?? ALL_SITES_KEY;
  const [nodes, setNodes] = useState<RegionNode[]>(() => cachedNodesValues.get(cacheKey) ?? []);
  const [loading, setLoading] = useState(!cachedNodesValues.has(cacheKey));
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    const cached = cachedNodesValues.get(cacheKey);
    if (cached) {
      setNodes(cached);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchNodesOnce(site)
      .then(rows => {
        if (cancelled) return;
        setNodes(rows);
        setError(null);
      })
      .catch(err => {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [site, cacheKey]);

  return { nodes, loading, error };
}

export type RegionNodeTreeNode = RegionNode & { children: RegionNodeTreeNode[] };

/**
 * Build a parent/child tree from the flat region-country node list off
 * `(parentKind, parentId)`. Identity is keyed by `${kind}:${id}` because
 * region- and country-id spaces can collide numerically. Orphans (parent not
 * present) surface at the top level so the tree never silently drops nodes.
 */
export function buildRegionNodeTree(flat: RegionNode[]): RegionNodeTreeNode[] {
  const byKey = new Map<string, RegionNodeTreeNode>();
  for (const n of flat) {
    byKey.set(regionNodeKey(n), { ...n, children: [] });
  }
  const roots: RegionNodeTreeNode[] = [];
  for (const node of byKey.values()) {
    // Parents are always regions (§3.1).
    const parentKey =
      node.parentKind && node.parentId != null
        ? regionNodeKey({ kind: node.parentKind, id: node.parentId })
        : null;
    if (parentKey && byKey.has(parentKey)) {
      byKey.get(parentKey)!.children.push(node);
    } else {
      roots.push(node);
    }
  }
  const sortRec = (nodes: RegionNodeTreeNode[]) => {
    nodes.sort(
      (a, b) =>
        (a.displayOrder ?? 0) - (b.displayOrder ?? 0) || (a.name ?? "").localeCompare(b.name ?? ""),
    );
    for (const n of nodes) sortRec(n.children);
  };
  sortRec(roots);
  return roots;
}
